import { TKeyMetrics, TSignalCountsOnSpeedRanges, TSummaryKeyMetrics, TSummarySignalCountsOnSpeedRanges } from './kpiTypes';

/**
 * Calculates adjusted speed metrics by creating a deep copy of sector metrics
 * and updating average laden and ballast speeds based on speed range distribution data.
 * 
 * @param sectorMetrics - The sector key metrics data from Redux store
 * @param speedRanges - The speed range signal counts data
 * @returns Adjusted metrics with calculated speeds or null if data is insufficient
 */
export const calculateAdjustedSpeedMetricsForSummary = (
    summaryKeyMetrics: TSummaryKeyMetrics | null,
    summarySignalCountsOnSpeedRanges: TSummarySignalCountsOnSpeedRanges
): TSummaryKeyMetrics | null => {
    // Early return if required data is missing
    if (
        !summaryKeyMetrics?.sectorListKeyMetrics?.length ||
        !summaryKeyMetrics.sectorListKeyMetrics[0]?.data ||
        !summarySignalCountsOnSpeedRanges?.sectorListSignalCountsOnSpeedRanges?.length ||
        !summarySignalCountsOnSpeedRanges.sectorListSignalCountsOnSpeedRanges[0]?.dataList
    ) {
        return null;
    }

    const speedRangeData = summarySignalCountsOnSpeedRanges.sectorListSignalCountsOnSpeedRanges[0].dataList;

    // Initialize speed calculation variables
    let totalLadenSpeedWithRange = 0;
    let totalBallastSpeedWithRange = 0;
    let totalLadenCount = 0;
    let totalBallastCount = 0;

    // Calculate weighted speed totals and counts
    speedRangeData.forEach((speedRange) => {
        const rangeLevel = speedRange.rangeLevel;
        const ladenCount = speedRange.countOfLaden ?? 0;
        const ballastCount = speedRange.countOfBallast ?? 0;

        totalLadenSpeedWithRange += ladenCount * rangeLevel;
        totalBallastSpeedWithRange += ballastCount * rangeLevel;
        totalLadenCount += ladenCount;
        totalBallastCount += ballastCount;
    });

    // Calculate average speeds (avoid division by zero)
    const averageLadenSpeed = totalLadenCount > 0 ? totalLadenSpeedWithRange / totalLadenCount : 0;
    const averageBallastSpeed = totalBallastCount > 0 ? totalBallastSpeedWithRange / totalBallastCount : 0;

    // Create deep copy to avoid modifying read-only Redux state
    const adjustedMetrics = JSON.parse(JSON.stringify(summaryKeyMetrics)) as TSummaryKeyMetrics;

    // Update calculated speed values
    adjustedMetrics.sectorListKeyMetrics![0].data!.averageLadenSpeed = averageLadenSpeed;
    adjustedMetrics.sectorListKeyMetrics![0].data!.averageBallastSpeed = averageBallastSpeed;

    return adjustedMetrics;
};

export const calculateAdjustedSpeedMetricsForVessel = (
    keyMetrics: TKeyMetrics | null,
    speedRanges: TSignalCountsOnSpeedRanges
): TKeyMetrics | null => {
    // Early return if required data is missing
    if (
        !keyMetrics ||
        !keyMetrics.data ||
        !speedRanges ||
        !speedRanges.dataList ||
        speedRanges.dataList.length === 0
    ) {
        return null;
    }

    const speedRangeData = speedRanges.dataList;

    // Initialize speed calculation variables
    let totalLadenSpeedWithRange = 0;
    let totalBallastSpeedWithRange = 0;
    let totalLadenCount = 0;
    let totalBallastCount = 0;

    // Calculate weighted speed totals and counts
    speedRangeData.forEach((speedRange) => {
        const rangeLevel = speedRange.rangeLevel;
        const ladenCount = speedRange.countOfLaden ?? 0;
        const ballastCount = speedRange.countOfBallast ?? 0;

        totalLadenSpeedWithRange += ladenCount * rangeLevel;
        totalBallastSpeedWithRange += ballastCount * rangeLevel;
        totalLadenCount += ladenCount;
        totalBallastCount += ballastCount;
    });

    // Calculate average speeds (avoid division by zero)
    const averageLadenSpeed = totalLadenCount > 0 ? totalLadenSpeedWithRange / totalLadenCount : 0;
    const averageBallastSpeed = totalBallastCount > 0 ? totalBallastSpeedWithRange / totalBallastCount : 0;

    // Create deep copy to avoid modifying read-only Redux state
    const adjustedMetrics = JSON.parse(JSON.stringify(keyMetrics)) as TKeyMetrics;

    // Update calculated speed values
    adjustedMetrics.data!.averageLadenSpeed = averageLadenSpeed;
    adjustedMetrics.data!.averageBallastSpeed = averageBallastSpeed;

    return adjustedMetrics;
};